import { useEffect, useMemo, useState, useRef } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import { api } from "./api";
import { disableAdminPush } from "./push";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Calculator from "./pages/Calculator";
import Admin from "./pages/Admin";
import AdminCalculator from "./pages/adminCalculator";
import PercentCalculator from "./pages/percentCalculator";
import BasfCalculator from "./pages/basfCalculator";
import ContractCompare from "./pages/ContractCompare";
import GdbuCalculator from "./pages/gdbuCalculator";
import AdminReferralQr from "./pages/AdminReferralQr";

function decodeRoleFromToken(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload?.role || "user";
  } catch {
    return "user";
  }
}

function decodeUserIdFromToken(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload?.sub || payload?.id || "";
  } catch {
    return "";
  }
}

function App() {
  const [health, setHealth] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("token") || "");
  const [role, setRole] = useState(() => (token ? decodeRoleFromToken(token) : "user"));
  const nav = useNavigate();
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [mobileAdminOpen, setMobileAdminOpen] = useState(false);
  const adminMenuRef = useRef(null);
  const burgerRef = useRef(null);
  const [mobileMenuPos, setMobileMenuPos] = useState({ top: 0, right: 12, width: 320 });
  const loc = useLocation();
  const [userId, setUserId] = useState(() => (token ? decodeUserIdFromToken(token) : ""));
  const [defaultCalcRoute, setDefaultCalcRoute] = useState("/");

  useEffect(() => {
    setAdminMenuOpen(false);
    setMobileNavOpen(false);
    setMobileAdminOpen(false);
  }, [loc.pathname]);

  useEffect(() => {
  if (token) {
    localStorage.setItem("token", token);
    setRole(decodeRoleFromToken(token));
    setUserId(decodeUserIdFromToken(token));
  } else {
    localStorage.removeItem("token");
    setRole("user");
    setUserId("");
  }
}, [token]);

function sanitizeDefaultRoute(route) {
  const allowed = new Set([
    "/",
    "/admin-calculator",
    "/percent-calculator",
    "/basf-calculator",
    "/contract-compare",
  ]);
  return allowed.has(route) ? route : "/";
}

useEffect(() => {
  if (!token || role !== "admin" || !userId) return;

  let cancelled = false;

  api.adminUser(userId)
    .then((d) => {
      if (cancelled) return;
      const r = sanitizeDefaultRoute(d?.user?.defaultCalculatorRoute || "/");
      setDefaultCalcRoute(r);
    })
    .catch(() => {
      setDefaultCalcRoute("/");
    });

  return () => {
    cancelled = true;
  };
}, [token, role, userId]);

  useEffect(() => {
  function onDocDown(e) {
    if (!adminMenuRef.current) return;
    if (!adminMenuRef.current.contains(e.target)) setAdminMenuOpen(false);
  }
  document.addEventListener("mousedown", onDocDown);
  return () => document.removeEventListener("mousedown", onDocDown);
}, []);

  useEffect(() => {
    api.health().then(setHealth).catch(() => setHealth(null));
  }, []);

  useEffect(() => {
    if (token) {
      localStorage.setItem("token", token);
      setRole(decodeRoleFromToken(token));
    } else {
      localStorage.removeItem("token");
      setRole("user");
    }
  }, [token]);

  const authed = !!token;

  useEffect(() => {
  if (!token || role !== "admin") return;

  const current = loc.pathname || "/";
  const target = sanitizeDefaultRoute(defaultCalcRoute || "/");

  // redirect away from login/signup
  if (current === "/login" || current === "/signup") {
    nav(target, { replace: true });
  }
}, [token, role, defaultCalcRoute, loc.pathname, nav]);


  const top = (
      <div className="topbar">
        <div className="left">
          {authed ? (
            <>
              <NavLink
                to="/"
                end
                className={({ isActive }) => (isActive ? "navLink navActive" : "navLink")}
                onClick={() => setMobileNavOpen(false)}
              >
                Calculator
              </NavLink>

              {role === "admin" ? (
                <div
                  className={`navDropdown ${adminMenuOpen ? "isOpen" : ""}`}
                  ref={adminMenuRef}
                >
                  <button
                    type="button"
                    className="navLink navDropdownTrigger"
                    onClick={() => setAdminMenuOpen((v) => !v)}
                    aria-haspopup="menu"
                    aria-expanded={adminMenuOpen}
                  >
                    Admin <span className="chev">▾</span>
                  </button>

                    <div
                        className="navMenu"
                        role="menu"
                        onClickCapture={() => setAdminMenuOpen(false)}
                      >

                    <NavLink
                      to="/admin"
                      role="menuitem"
                      className={({ isActive }) => (isActive ? "navMenuItem navMenuActive" : "navMenuItem")}
                      onClick={() => setAdminMenuOpen(false)}
                    >
                      Admin
                    </NavLink>

                    <NavLink
                      to="/admin-calculator"
                      role="menuitem"
                      className={({ isActive }) => (isActive ? "navMenuItem navMenuActive" : "navMenuItem")}
                      onClick={() => setAdminMenuOpen(false)}
                    >
                      Techemet Calculator
                    </NavLink>

                    <NavLink
                      to="/percent-calculator"
                      role="menuitem"
                      className={({ isActive }) => (isActive ? "navMenuItem navMenuActive" : "navMenuItem")}
                      onClick={() => setAdminMenuOpen(false)}
                    >
                      Price Calculator (%)
                    </NavLink>
                    <NavLink
                      to="/basf-calculator"
                      role="menuitem"
                      className={({ isActive }) => (isActive ? "navMenuItem navMenuActive" : "navMenuItem")}
                      onClick={() => setAdminMenuOpen(false)}
                    >
                      BASF Calculator
                    </NavLink>
                    <NavLink to="/gdbu-calculator" 
                      role="menuitem"
                      className={({ isActive }) => (isActive ? "navMenuItem navMenuActive" : "navMenuItem")}
                      onClick={() => setAdminMenuOpen(false)}
                    >
                      GDBU Calculator
                    </NavLink>

                    <NavLink
                      to="/contract-compare"
                      role="menuitem"
                      className={({ isActive }) => (isActive ? "navMenuItem navMenuActive" : "navMenuItem")}
                      onClick={() => setAdminMenuOpen(false)}
                    >
                      Contract Compare
                    </NavLink>

                    <NavLink
                      to="/admin/referrals"
                      role="menuitem"
                      className={({ isActive }) => (isActive ? "navMenuItem navMenuActive" : "navMenuItem")}
                      onClick={() => setAdminMenuOpen(false)}
                    >
                      Referral QR Codes
                    </NavLink>

                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>

          <div className="right">
            {authed ? (
              <>
              <button
                ref={burgerRef}
                type="button"
                className="hamburger"
                onClick={() => {
                  setMobileNavOpen((v) => {
                    const next = !v;

                    if (next && burgerRef.current) {
                      const r = burgerRef.current.getBoundingClientRect();
                      const vw = window.innerWidth;

                      // width: clamp (fits on small phones)
                      const width = Math.min(280, Math.max(260, vw - 0));

                      // place menu under the button, aligned to right edge of button
                      const right = Math.max(12, Math.min(vw - 12 - width, vw - r.right));
                      const top = Math.round(r.bottom + 10);

                      setMobileMenuPos({ top, right, width });
                    }

                    return next;
                  });
                }}
                aria-label="Menu"
                aria-expanded={mobileNavOpen}
              >
                ☰
              </button>


              <button
                type="button"
                className="logoutBtnDesktop"
                onClick={async () => {
                  try { await api.pushUnsubscribeAll(); } catch {}
                  try { await disableAdminPush(); } catch {}
                  setToken("");
                  nav("/login");
                }}
              >
                Logout
              </button>


              </>
            ) : (
              <>
                <NavLink to="/login" className={({ isActive }) => (isActive ? "navLink navActive" : "navLink")}>
                  Login
                </NavLink>
                <NavLink to="/signup" className={({ isActive }) => (isActive ? "navLink navActive" : "navLink")}>
                  Signup
                </NavLink>
              </>
            )}
          </div>
      </div>
    );

  return (
    <div className="App">
      {top}
      {authed && mobileNavOpen ? (
          <div
            className="mobileNavRoot"
            onMouseDown={() => setMobileNavOpen(false)}
            role="presentation"
          >
            <div
              className="mobileNavPanel"
              style={{
                top: mobileMenuPos.top,
                right: mobileMenuPos.right,
                width: mobileMenuPos.width
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >

              <NavLink
                to="/"
                end
                className={({ isActive }) => (isActive ? "mobileNavItem mobileActive" : "mobileNavItem")}
                onClick={() => setMobileNavOpen(false)}
              >
                Calculator
              </NavLink>

              {role === "admin" ? (
                <div className="mobileGroup">
                  <button
                    type="button"
                    className="mobileNavItem mobileGroupTrigger"
                    onClick={() => setMobileAdminOpen((v) => !v)}
                    aria-expanded={mobileAdminOpen}
                  >
                    Admin <span className="chev">{mobileAdminOpen ? "▴" : "▾"}</span>
                  </button>

                  {mobileAdminOpen ? (
                    <div className="mobileSub">
                      <NavLink
                        to="/admin"
                        className={({ isActive }) => (isActive ? "mobileNavItem mobileActive" : "mobileNavItem")}
                        onClick={() => setMobileNavOpen(false)}
                      >
                        Admin
                      </NavLink>

                      <NavLink
                        to="/admin-calculator"
                        className={({ isActive }) => (isActive ? "mobileNavItem mobileActive" : "mobileNavItem")}
                        onClick={() => setMobileNavOpen(false)}
                      >
                        Techemet Calculator
                      </NavLink>

                      <NavLink
                        to="/percent-calculator"
                        className={({ isActive }) => (isActive ? "mobileNavItem mobileActive" : "mobileNavItem")}
                        onClick={() => setMobileNavOpen(false)}
                      >
                        Price Calculator (%)
                      </NavLink>
                      <NavLink
                        to="/basf-calculator"
                        className={({ isActive }) => (isActive ? "mobileNavItem mobileActive" : "mobileNavItem")}
                        onClick={() => setMobileNavOpen(false)}
                      >
                        BASF Calculator
                      </NavLink>
                      <NavLink
                        to="/gdbu-calculator"
                        className={({ isActive }) => (isActive ? "mobileNavItem mobileActive" : "mobileNavItem")}
                        onClick={() => setMobileNavOpen(false)}
                      >
                        GDBU Calculator
                      </NavLink>
                      <NavLink
                        to="/contract-compare"
                        className={({ isActive }) => (isActive ? "mobileNavItem mobileActive" : "mobileNavItem")}
                        onClick={() => setMobileNavOpen(false)}
                      >
                        Contract Compare
                      </NavLink>

                      <NavLink
                        to="/admin/referrals"
                        className={({ isActive }) => (isActive ? "mobileNavItem mobileActive" : "mobileNavItem")}
                        onClick={() => setMobileNavOpen(false)}
                      >
                        Referral QR Codes
                      </NavLink>

                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* Logout INSIDE mobile menu */}
              <button
                type="button"
                className="mobileLogoutBtn"
                onClick={async () => {
                  try { await api.pushUnsubscribeAll(); } catch {}
                  try { await disableAdminPush(); } catch {}
                  setToken("");
                  setMobileNavOpen(false);
                  nav("/login");
                }}
              >
                Logout
              </button>


            </div>
          </div>
        ) : null}

      <div className="container">
        <div className="hero">
          <div className="heroTitle">
              <span className="badge" />
              Precious Metals Calculator
              </div>
              <p className="heroSub">
              Platinum • Palladium • Rhodium.
              </p>
              </div>
    
              <Routes>
                <Route path="/login" element={<Login onToken={setToken} />} />
                <Route path="/signup" element={<Signup />} />

                <Route
                  path="/"
                  element={authed ? <Calculator authed={authed} /> : <Navigate to="/login" replace />}
                />

                <Route
                  path="/admin"
                  element={
                    authed && role === "admin"
                      ? <Admin authed={authed} role={role} />
                      : <Navigate to="/login" replace />
                  }
                />

                <Route
                  path="/admin-calculator"
                  element={
                    authed && role === "admin"
                      ? <AdminCalculator authed={authed} role={role} />
                      : <Navigate to="/login" replace />
                  }
                />
                  <Route
                    path="/percent-calculator"
                    element={
                      authed && role === "admin"
                        ? <PercentCalculator authed={authed} role={role} />
                        : <Navigate to="/login" replace />
                    }
                  />
                  <Route
                    path="/basf-calculator"
                    element={
                      authed && role === "admin"
                        ? <BasfCalculator authed={authed} role={role} />
                        : <Navigate to="/login" replace />
                    }
                  />
                  <Route
                    path="/gdbu-calculator"
                    element={
                      authed && role === "admin"
                        ? <GdbuCalculator authed={authed} role={role} />
                        : <Navigate to="/login" replace />
                    }
                  />
                  <Route
                    path="/contract-compare"
                    element={
                      authed && role === "admin"
                        ? <ContractCompare authed={authed} role={role} />
                        : <Navigate to="/login" replace />
                    }
                  />
                  <Route
                    path="/admin/referrals"
                    element={
                      authed && role === "admin"
                        ? <AdminReferralQr />
                        : <Navigate to="/login" replace />
                    }
                  />

                {/* optional: catch-all */}
                <Route path="*" element={<Navigate to={authed ? "/" : "/login"} replace />} />
              </Routes>
      </div>
    </div>
  );
}

export default App;